import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { WordReplacement } from './text-processor.interface';
import { DictionaryService } from '../dictionary/dictionary.service';
import { UserReplacementRepository } from '../data-access-layer/user-replacements-repository.service';
import { SuggestionRepository } from '../data-access-layer';
import { ReplacementResponse } from '../core/dto/replacement';
import { paragraphDTO, editStateEnum, spellingStateEnum, tokenDTO, sentenceDTO } from '@capiche/ts-data-models-shared';
import { CapicheLoggerService } from 'capiche-logger';
import { CapicheMixpanelService } from 'capiche-mixpanel';
import { clientUserDictionaryEntry } from '@capiche/ts-data-models-client';
import { UsageCounterService } from '../usage-counter/usage-counter.service';

/** Tagged paragraph result emitted by TextProcessorService. */
export interface ParagraphResult {
  elId: string;
  para: paragraphDTO;
}

/**
 * This service is responsible for processing the text input by the user.
 * It should be input medium (editor, text field, etc) agnostic.
 *
 * Callers pass `elId` so results are tagged and can be routed back to the
 * correct element's state in ProcessingPipelineService.
 */
/** CSW-1736: one entry per paragraph identity while processInput() is running. */
interface InFlightEntry {
  requestId: number;
  seqId: number | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class TextProcessorService {
  logger = new CapicheLoggerService('text-processor.service:', true);

  /** Emits after local trie lookup completes (fast, no server call). */
  readonly localOutput$ = new Subject<ParagraphResult>();

  /** Emits after Phase 1 server response and again after Phase 2 GPT enrichment. */
  readonly serverOutput$ = new Subject<ParagraphResult>();

  private lookupStatusSubject = new Subject<boolean>();
  public lookupState$ = this.lookupStatusSubject.asObservable();

  // Per-paragraph stale-response guard.
  // Key: `${elId}:${srcId}` — one entry per paragraph identity (not per text),
  // so the entry is overwritten whenever the same paragraph fires a new request,
  // regardless of whether the text changed.  This prevents stale responses from
  // passing the guard when paragraph text changes (changing the text also changes
  // the old text-based key, so the old entry was never overwritten).
  private inFlightIds = new Map<string, InFlightEntry>();
  private nextRequestId: number = 0;

  private _userReplacementRepository: UserReplacementRepository;
  private _suggestionRepository: SuggestionRepository;

  constructor(
    private clientDictionary: DictionaryService,
    private mixpanel: CapicheMixpanelService,
    private usageCounter: UsageCounterService,
    userReplacementRepository: UserReplacementRepository,
    suggestionRepository: SuggestionRepository,
  ) {
    this.logger.log('constructor');
    this._userReplacementRepository = userReplacementRepository;
    this._suggestionRepository = suggestionRepository;
  }

  /**
   * CSW-1736: true while a processInput() call for this paragraph, at this
   * seqId, has not yet completed (both phases).  The pipeline uses it to avoid
   * re-issuing identical requests on every snapshot while an earlier one is
   * still pending — on a multi-paragraph document those resends compounded
   * into hundreds of getSuggestions/enrichSuggestions calls per minute.
   * A changed paragraph carries a new seqId and is never reported in flight.
   */
  isInFlight(elId: string, srcId: string | undefined, seqId: number | undefined, input: string): boolean {
    const entry: InFlightEntry | undefined = this.inFlightIds.get(this.paraKeyOf(elId, srcId, input));
    return entry !== undefined && entry.seqId === seqId;
  }

  private paraKeyOf(elId: string, srcId: string | undefined, input: string): string {
    return `${elId}:${srcId ?? input}`; // this looksflakey if no srcId is supplied -I think it implies srcId should really be mandatory
  }

  async processInput(inputParagraph: paragraphDTO, elId: string) {
    // Assign a unique request ID for this paragraph so we can detect if a newer
    // request fires for the same paragraph before this one resolves (stale guard).
    const requestId: number = ++this.nextRequestId;
    const paraKey: string = this.paraKeyOf(elId, inputParagraph.srcId, inputParagraph.input);
    this.inFlightIds.set(paraKey, { requestId, seqId: inputParagraph.seqId });

    try {
      await this.runProcessInput(inputParagraph, elId, requestId, paraKey);
    } finally {
      // CSW-1736: release the in-flight entry, but only if it is still ours —
      // a newer request for the same paragraph must keep its own entry.
      if (this.inFlightIds.get(paraKey)?.requestId === requestId) {
        this.inFlightIds.delete(paraKey);
      }
    }
  }

  private async runProcessInput(inputParagraph: paragraphDTO, elId: string, requestId: number, paraKey: string) {
    const t0: number = performance.now();

    this.logger.log(`processInput() input0: reqId=${requestId} elId=${elId} para=`, inputParagraph);
    this.lookupStatusSubject.next(true);

    const intmediateParagraph: paragraphDTO = this.buildLocalParagraph(inputParagraph);
    this.logger.log(`processInput() local0: reqId=${requestId} elId=${elId} para=`, intmediateParagraph);
    this.localOutput$.next({ elId, para: intmediateParagraph });
    this.logger.log(`processInput() phase0: reqId=${requestId} elId=${elId} ${Math.round(performance.now() - t0)}ms`);

    // ── Phase 1: dictionary + phonetic lookup (fast, no GPT) ──────────────────
    // TODO [CSW-XXXX]: adaptive rate limiting for Phase 1 / Phase 2 server calls.
    // During sustained fast typing, requests pile up and GPT Phase 2 responses
    // arrive stale (discarded by inFlightIds guard). The stale guard keeps
    // correctness but wastes server cost. Proposed: adaptive inter-query interval
    // starting at ~100ms, ramping to 3-5s during furious typing, decaying back to
    // fast when typing slows — same rolling-interval principle as the adaptive
    // debounce in InputHandlerService. AbortController is NOT suitable (GCF does
    // not support request cancellation mid-execution).
    const t1: number = performance.now();

    // Server contract: send only `replaced` tokens per sentence; strip everything else.
    // The server re-tokenises each sentence from scratch (Tokens.fromString(input))
    // and merges preserved replaced tokens back in — non-replaced tokens in the query
    // are discarded server-side regardless, so sending them is wasteful.
    // The client-side filters (applyPhase1Filter / applyPhase2Filter) are the
    // correct place to prevent server results from overwriting already-assessed
    // state — not the server query.
    const serverQuery: paragraphDTO = {
      ...intmediateParagraph,
      sentences: intmediateParagraph.sentences.map((s: sentenceDTO) => ({
        ...s,
        tokens: s.tokens.filter((t: tokenDTO) => t.editState === editStateEnum.replaced),
      })),
    };

    this.mixpanel.track('getSuggestions', { query: JSON.stringify(serverQuery) });
    // CSW-1643: batched usage counter (per-user calls/day), independent of Mixpanel.
    this.usageCounter.recordGetSuggestions();

    this.logger.log(`processInput() query1: reqId=${requestId} elId=${elId} para=`, serverQuery);

    let mergedResponseParagraph: paragraphDTO;
    try {
      const server1ResponseParagraph: paragraphDTO = await this._suggestionRepository.getSuggestions(serverQuery);
      this.logger.log(`processInput() serve1: reqId=${requestId} elId=${elId} para=`, server1ResponseParagraph);
      this.logger.log(`processInput() phase1: reqId=${requestId} elId=${elId} ${Math.round(performance.now() - t1)}ms`);

      mergedResponseParagraph = this.applyPhase1Filter(server1ResponseParagraph, inputParagraph);
      this.logger.log(`processInput() merge1: reqId=${requestId} elId=${elId} para=`, mergedResponseParagraph);
      this.logger.log(`processInput() phase1+ reqId=${requestId} elId=${elId} ${Math.round(performance.now() - t1)}ms`);
    } catch (err: any) {
      this.logger.log(`processInput() catch1: reqId=${requestId} elId=${elId} ${err?.message ?? String(err)}`);
      this.logger.log(`processInput() phase1! reqId=${requestId} elId=${elId} ${Math.round(performance.now() - t1)}ms`);
      this.lookupStatusSubject.next(false);
      return;
    }

    // Stale-response check: discard if a newer request superseded this one.
    if (this.inFlightIds.get(paraKey)?.requestId !== requestId) {
      this.logger.log(
        `processInput() stale1: reqId=${requestId} elId=${elId} > ${this.inFlightIds.get(paraKey)?.requestId}`,
      );
      this.lookupStatusSubject.next(false);
      return;
    }

    this.logger.log(`processInput() emits1: reqId=${requestId} elId=${elId} para=`, mergedResponseParagraph);
    this.emitServerResult(elId, mergedResponseParagraph);
    this.logger.log(`processInput() final1: reqId=${requestId} elId=${elId} ${Math.round(performance.now() - t1)}ms`);

    // ── Phase 2: GPT enrichment ────────────────────────────────────────────────
    const t2 = performance.now();

    this.logger.log(`processInput() query2: reqId=${requestId} elId=${elId} para=`, mergedResponseParagraph);

    try {
      const s2ResponsePara: paragraphDTO = await this._suggestionRepository.enrichSuggestions(mergedResponseParagraph);
      this.logger.log(`processInput() serve2: reqId=${requestId} elId=${elId} para=`, s2ResponsePara);
      this.logger.log(`processInput() phase2: reqId=${requestId} elId=${elId} ${Math.round(performance.now() - t2)}ms`);

      const enrichedParagraph: paragraphDTO = this.applyPhase2Filter(s2ResponsePara, inputParagraph);
      this.logger.log(`processInput() merge2: reqId=${requestId} elId=${elId} para=`, enrichedParagraph);
      this.logger.log(`processInput() phase2+ reqId=${requestId} elId=${elId} ${Math.round(performance.now() - t2)}ms`);

      // No stale guard here — mergeResult() applies seqId-level matching so only
      // sentences whose seqId still matches the store are updated.  Dropping the
      // paragraph-level reqId check lets enrichments for unchanged sentences surface
      // immediately rather than waiting for the newest in-flight Phase 2 to complete.
      this.logger.log(`processInput() emits2: reqId=${requestId} elId=${elId} para=`, enrichedParagraph);
      this.emitServerResult(elId, enrichedParagraph);
      this.logger.log(`processInput() final2: reqId=${requestId} elId=${elId} ${Math.round(performance.now() - t2)}ms`);
    } catch (err: any) {
      this.logger.log(`processInput() catch2: reqId=${requestId} elId=${elId} ${err?.message ?? String(err)}`);
      this.logger.log(`processInput() phase2! reqId=${requestId} elId=${elId} ${Math.round(performance.now() - t2)}ms`);
      // Phase 1 result is already applied — Phase 2 failure is non-fatal.
    } finally {
      this.lookupStatusSubject.next(false);
    }
    this.logger.log(`processInput() total2: reqId=${requestId} elId=${elId} ${Math.round(performance.now() - t0)}ms`);
  }

  /**
   * Applies the local trie lookup to all new tokens in a paragraph and returns
   * the intermediate paragraphDTO (editState: local). Pure function — no side effects.
   */
  private buildLocalParagraph(inputParagraph: paragraphDTO): paragraphDTO {
    return {
      ...inputParagraph,
      editState: editStateEnum.local,
      sentences: inputParagraph.sentences.map((sentence: sentenceDTO) => {
        // Trie pass (Phase 0): run only new tokens through the client dictionary.
        // Sentences that are already `local` (trie-assessed) or `checked` (server-assessed)
        // are returned unchanged — no re-trieing.
        //
        // Within a `new` sentence, individual tokens are similarly guarded:
        // `replaced` tokens (user accepted a suggestion), `local`, and `checked` tokens
        // are all preserved. Only `new` tokens are run through tokenDTOLookup.
        //
        // `replaced` tokens inside a new sentence do NOT need to bubble up to make the
        // sentence `new` — the text-change that triggered the replacement already produced
        // a DIRTY-zone sentence with editState `new` (see preserveSentenceState).
        // The token-level guard `token.editState !== new` is sufficient to protect them.
        //
        // This result is emitted to localOutput$ as a fast-path intermediate; it is not
        // stored in the state store.
        return sentence.editState !== editStateEnum.new
          ? sentence
          : {
              ...sentence,
              editState: editStateEnum.local,
              tokens: sentence.tokens.map((token: tokenDTO) => {
                return token.editState !== editStateEnum.new ? token : this.tokenDTOLookup(token);
              }),
            };
      }),
    };
  }

  /**
   * Selectively merge a server result onto the original paragraph.
   *
   * Matching is by character position (sentence.index / token.index), NOT by
   * ordinal array position.  The server may return a different token count or
   * sentence arrangement from the query (e.g. re-tokenising from the sentence
   * text despite us sending actual tokens).  Ordinal matching would silently
   * mis-align in those cases and clobber checked token state.  Position-based
   * matching is the correct "merge token by token" contract: each token in the
   * result is matched to the original token at the same character position; if
   * no original token exists at that position, `mergeToken` receives undefined
   * and the result token is used as-is by convention.
   *
   * `mergeToken` is called for every token in the result.  It receives the
   * original token at the same position (or undefined if none exists) and the
   * result token, and returns the token to use in the merged output.  The caller
   * controls exactly what fields are preserved vs overwritten.
   *
   * Pure function — returns a new paragraphDTO.
   */
  private filterResultTokens(
    result: paragraphDTO,
    original: paragraphDTO,
    mergeToken: (origTok: tokenDTO | undefined, resultTok: tokenDTO) => tokenDTO,
  ): paragraphDTO {
    // Index original sentences by their absolute character position.
    const origSentByPos = new Map<number, sentenceDTO>(original.sentences.map((s) => [s.index, s]));

    const mergedSentences = result.sentences.map((resultSent) => {
      const origSent = origSentByPos.get(resultSent.index);
      if (!origSent) return resultSent;

      // Index original tokens by sentence-local character position.
      const origTokByPos = new Map<number, tokenDTO>(origSent.tokens.map((t) => [t.index, t]));

      const mergedTokens = resultSent.tokens.map((resultTok) =>
        mergeToken(origTokByPos.get(resultTok.index), resultTok),
      );

      return { ...resultSent, tokens: mergedTokens };
    });

    return { ...result, sentences: mergedSentences };
  }

  /**
   * Phase 1 filter — preserves tokens that are already `checked`, `replaced`, or `discarded`.
   *
   * Phase 1 (dictionary/phonetic) has no contextual knowledge and will
   * classify context-dependent tokens (e.g. "sow" at sentence-start) as
   * `correct`.  Letting Phase 1 overwrite a prior Phase 2 `contextual` result
   * causes a ~0.5 s highlight dropout between Phase 1 and Phase 2 responses.
   *
   * `replaced` tokens (user-accepted suggestions) are also fully preserved by
   * Phase 1 — it has no authority over user word choice.  Phase 2 may still
   * update replaced tokens within the { correct, contextual } domain; see
   * applyPhase2Filter for the full policy.
   *
   * `discarded` tokens (GPT removed them from its sentence rewrite) are Phase 2-
   * exclusive — Phase 1 has no authority over them and must not clear the state.
   * Phase 2 will re-assess on its next run: if the context has changed enough
   * that the word now belongs, Phase 2 will return a non-discarded state and the
   * token will be restored. Every user edit that changes sentence text triggers a
   * new Phase 2 run, so discarded state is always re-confirmed or cleared promptly.
   *
   * Phase 1 also never produces sentence-level suggestions — those are Phase 2's
   * domain.  The existing suggestion from a prior Phase 2 run is preserved so it
   * isn't erased while the new Phase 2 request is still in flight.
   */
  private applyPhase1Filter(phase1: paragraphDTO, original: paragraphDTO): paragraphDTO {
    const filtered = this.filterResultTokens(phase1, original, (orig, result) => {
      if (!orig) return result;
      if (
        orig.editState === editStateEnum.checked ||
        orig.editState === editStateEnum.replaced ||
        orig.editState === editStateEnum.discarded
      ) {
        // Phase 1 has no authority over already-assessed tokens: preserve spellingState,
        // editState, and suggestions from the stored original.
        return { ...result, spellingState: orig.spellingState, editState: orig.editState, suggestions: orig.suggestions };
      }
      return result;
    });

    // Phase 1 never returns sentence-level suggestions (sentence.suggestion is always "").
    // Preserve any suggestion set by a prior Phase 2 run so it isn't erased while the
    // new Phase 2 request for this seqId is still in flight.
    const origSentByIndex = new Map(original.sentences.map((s) => [s.index, s]));
    return {
      ...filtered,
      sentences: filtered.sentences.map((s) => ({
        ...s,
        suggestion: origSentByIndex.get(s.index)?.suggestion ?? s.suggestion,
      })),
    };
  }

  /**
   * Merge Phase 2 (GPT enrichment) results into the stored paragraph.
   *
   * Phase 2 is a CONTEXTUAL engine, not a spelling checker. Its authority over
   * `spellingState` and `editState` is limited to the { correct, contextual }
   * domain — deciding whether a dictionary-valid word is appropriate in context.
   *
   * Spelling facts (misspelled, unknown) are established by Phase 1 and treated
   * as immutable by Phase 2 — for now. This restriction exists because GPT
   * suggestions are not yet reliably distinguishable from hallucinations or
   * repetitions of the input. When that distinction can be made with confidence,
   * Phase 2 could be granted authority over spelling facts too.
   *
   * However, Phase 2 IS permitted to update `token.suggestions` even for spelling-
   * domain tokens (misspelled / unknown).  The server's updateTokenByGptSuggestions
   * adds the contextual GPT correction to the top of the suggestion list (e.g.
   * "sessions" for "sjesjins") while leaving spellingState unchanged.  Blocking
   * suggestions here would silently discard that GPT insight — the token popup
   * would only show the phonetic spell-checker suggestion, not the contextually
   * correct word.
   *
   * Token-by-token merge policy (keyed on the original token's editState):
   *
   *   discarded  Phase 2 removed this token from its sentence rewrite.
   *              Always let through — discarded is a Phase 2-exclusive state
   *              that Phase 1 never produces.
   *
   *   checked    Phase 1 has fully assessed this token (spelling fact established).
   *   replaced   The user explicitly accepted this word via a suggestion.
   *
   *              correct ↔ contextual transition: Phase 2 wins fully (including
   *                spellingState and suggestions).
   *              All other mutations: Phase 2 may not change spellingState or
   *                editState, but CAN update token.suggestions (GPT contextual
   *                correction added to the top of the list by the server).
   *
   *   new/local  Not yet fully assessed. Phase 2 wins unconditionally.
   */
  private applyPhase2Filter(phase2: paragraphDTO, original: paragraphDTO): paragraphDTO {
    const contextualDomain = new Set<string>([spellingStateEnum.correct, spellingStateEnum.contextual]);
    return this.filterResultTokens(phase2, original, (orig, result) => {
      if (!orig) return result;
      if (result.editState === editStateEnum.discarded) return result; // Phase 2 wins unconditionally
      if (orig.editState === editStateEnum.checked || orig.editState === editStateEnum.replaced) {
        if (contextualDomain.has(orig.spellingState ?? '') && contextualDomain.has(result.spellingState ?? '')) {
          if (orig.editState === editStateEnum.replaced) {
            if (result.spellingState === spellingStateEnum.contextual) {
              // Phase 2 found a contextual correction on a replaced token (e.g. "with" → "With").
              // Clear replaced → checked so the token displays as contextual (blue) with suggestion.
              return { ...result, editState: editStateEnum.checked };
            }
            // Phase 2 confirmed correct — keep replaced state intact.
            // User explicitly chose this word; no further action needed.
            return orig;
          }
          // checked: correct ↔ contextual — Phase 2 wins fully.
          return result;
        }
        // Spelling fact (misspelled / unknown): Phase 2 cannot change spellingState or
        // editState, but its suggestions (GPT contextual correction at the top of the list)
        // are authoritative — use result.suggestions, preserve orig state fields.
        return { ...result, spellingState: orig.spellingState, editState: orig.editState };
      }
      return result; // new / local → Phase 2 wins
    });
  }

  /**
   * @param token a single tokenDTO
   * Looks up the word in the local dictionary and enriches it with
   * spelling suggestions + state.
   * @returns tokenDTO enriched with dictionary data
   *
   * if the token is not found in the dictionary, it will be marked as unknown
   * if the token is found and marked as misspelled, it will be marked as misspelled
   * if the token is found and marked as correct, it will be marked as correct
   * if the token is found and marked as contextual, it will be marked as correct (special case - see below)
   * if the token has a state other than those in spellingStateEnum, it will be marked as unknown (safety fallback for invalid data)
   *
   * if the token is found and has suggestions, they will be added to the tokenDTO
   *
   * spellingState.unknown means either not in client dictionary or in client dictionary with an invalid spellingState value
   * editState is always set to local by this function, since it's only doing local enrichment
   *
   * spellingState.correct returned for words in the client dictionary marked as correct OR contextual
   * spellingState.misspelled returned for words in the client dictionary marked as misspelled
   * spellingState.contextual NEVER RETURNED by this function (stored contextual entries are coerced to correct)
   * all other valid spellingState values stored in the dictionary are returned as-is
   * invalid spellingState values stored in the dictionary are treated as unknown and returned as spellingState.unknown
   *
   */
  private tokenDTOLookup(token: tokenDTO): tokenDTO {
    const entry: clientUserDictionaryEntry | null = this.clientDictionary.getEntry(token.input.toLocaleLowerCase());
    const editState: editStateEnum = editStateEnum.local;
    // special case: contextual -> correct
    // the dictionary can't know if the word is actually correct in context
    // so it's sensible to treat it as correct until we know more
    // TODO: all existing data in firestore should be updated to reflect this change
    //       raise a ticket to do this
    //       https://capiche.atlassian.net/browse/CSW-1214
    const spellingState: spellingStateEnum =
      entry?.spellingState === spellingStateEnum.contextual
        ? spellingStateEnum.correct
        : toSpellingState(entry?.spellingState);

    //this.logger.log(`tokenDTOLookup: ${token.input} -> ${editState}, ${spellingState}`);

    return {
      ...token,
      editState,
      spellingState,
      suggestions: entry?.suggestions ?? undefined,
    };
  }

  /**
   * Updates the local dictionary cache and emits a tagged server result.
   *
   * @param elId  — element the result belongs to (for pipeline routing)
   * @param para  — fully enriched paragraphDTO from the server
   */
  private emitServerResult(elId: string, para: paragraphDTO): void {
    // this.logger.log('emitServerResult:', elId, para);
    if (!para) {
      this.logger.error('emitServerResult: backend returned undefined or null — dropping result');
      this.lookupStatusSubject.next(false);
      return;
    }

    if (!para.sentences) {
      // Server response is missing the sentences array — log the raw response so we can
      // diagnose the server-side shape mismatch, then drop the result rather than crash.
      this.logger.error(
        'emitServerResult: para.sentences is missing — server response may be malformed or wrapped. Raw para:',
        JSON.stringify(para),
      );
      this.lookupStatusSubject.next(false);
      return;
    }

    // Seed the client dictionary with newly-checked tokens.
    this.clientDictionary.tokenDTOaddEntries(
      para.sentences.flatMap((sentence: sentenceDTO) =>
        sentence.tokens.filter((token: tokenDTO) => token.editState === editStateEnum.checked),
      ),
    );
    this.serverOutput$.next({ elId, para });
    this.lookupStatusSubject.next(false);
  }

  /**
   * takes a WordReplacement object and stores it in the user's replacement data
   * updates the dictionary
   * @param replacementWord
   */
  async replaceWord(replacementWord: WordReplacement): Promise<void> {
    this.logger.log('replaceWord', replacementWord);

    // Store replaced input
    const response = await this._userReplacementRepository.storeReplacementData(replacementWord);
    if (!response) {
      this.logger.warn('replaceWord skipped: no response (probably not logged in)');
      return;
    }

    const replacement = response.body as ReplacementResponse;
    if (!replacement?.attributes) {
      this.logger.error('replaceWord failed: response missing attributes', response);
      return;
    }

    // TODO: this was commented when working on CSW-1205
    // it's not clear why the dictionary is updated here
    // .. except to reflect the user replacement and how that may impact the suggestion ranks
    // I'm going to comment this out for now raise a ticket and then come back to it
    // https://capiche.atlassian.net/browse/CSW-1213
    // Update dictionary
    // this.clientDictionary.addEntries([replacement.attributes]);
  }
}

// helpers for safe casting
export function toSpellingState(value: string | null | undefined): spellingStateEnum {
  if (!value) return spellingStateEnum.unknown;
  return (Object.values(spellingStateEnum) as string[]).includes(value)
    ? (value as spellingStateEnum)
    : spellingStateEnum.unknown;
}

export function toEditState(value: string | null | undefined): editStateEnum {
  if (!value) return editStateEnum.new;
  return (Object.values(editStateEnum) as string[]).includes(value) ? (value as editStateEnum) : editStateEnum.invalid;
}
